// CookingChip — the app-level "still cooking" indicator. Mounted once in
// Shell so it survives tab switches, scrolling, and route changes. Reads
// the module-level generationTracker; renders nothing when no generation
// is in flight.
//
// Replaces the old inline cooking banner that lived inside the per-tab
// AtelierTabBody and vanished whenever that component unmounted.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getActiveGenerations, subscribe, endGeneration, type ActiveGen,
} from "../lib/generationTracker";

function formatEta(seconds: number): string {
  if (seconds < 60) return `~${seconds}s`;
  const m = seconds / 60;
  return m < 1.5 ? "~1 min" : `~${Math.round(m)} min`;
}

export function CookingChip() {
  const nav = useNavigate();
  const [gens, setGens] = useState<ActiveGen[]>(getActiveGenerations());
  const [, tick] = useState(0);

  // Re-render on tracker changes.
  useEffect(() => subscribe(() => setGens(getActiveGenerations())), []);
  // Re-render once a second while something is cooking, for the timer.
  useEffect(() => {
    if (gens.length === 0) return;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [gens.length]);

  if (gens.length === 0) return null;

  // Show the freshest generation; if more than one, note the overflow.
  const g = gens[0];
  const elapsed = Math.floor((Date.now() - g.startedAt) / 1000);
  const overTime = elapsed > g.etaSeconds;
  const ratio = Math.min(1, elapsed / g.etaSeconds);

  return (
    <div
      className="fixed bottom-6 right-6 z-[55] rounded-md flex items-center gap-4"
      role="status"
      aria-live="polite"
      style={{
        background: "linear-gradient(135deg, #fff7e6 0%, #f9e8cf 100%)",
        border: "2px solid #c25538",
        padding: "14px 18px",
        minWidth: 320,
        maxWidth: 420,
        boxShadow: "0 16px 36px -10px rgba(45,28,20,0.42), 0 0 0 5px rgba(194,85,56,0.10)",
        animation: "cookHalo 2400ms ease-in-out infinite",
      }}
    >
      <span
        aria-hidden
        className="inline-block w-3.5 h-3.5 rounded-full shrink-0"
        style={{
          background: "#c25538",
          animation: "cookDot 1400ms ease-in-out infinite",
          boxShadow: "0 0 0 4px rgba(194,85,56,0.22)",
        }}
      />
      <div className="flex flex-col leading-tight min-w-0 flex-1">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase font-semibold text-terracotta tabular-nums">
          ⊙ Still cooking · {elapsed}s of {formatEta(g.etaSeconds)}
          {gens.length > 1 ? ` · +${gens.length - 1} more` : ""}
        </span>
        <span className="font-serif italic text-[14px] text-ink-body truncate">
          {overTime
            ? `Taking longer than usual — long ${g.label.toLowerCase()} runs sometimes run +50%`
            : `AI is working on your ${g.label.toLowerCase()}`}
        </span>
        <div className="mt-1.5 h-1 w-full bg-hairline-bold/40 rounded-full overflow-hidden">
          <div
            className={overTime ? "h-full bg-foil animate-pulse" : "h-full bg-terracotta transition-[width] duration-700"}
            style={{ width: `${ratio * 100}%` }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <button
          type="button"
          onClick={() => nav(`/atelier/${g.sessionId}?tab=${g.tab}`)}
          className="px-3 py-1.5 font-mono text-[10px] tracking-[0.16em] uppercase rounded-sm border border-terracotta bg-white text-terracotta hover:bg-terracotta hover:text-white transition-colors whitespace-nowrap"
        >
          Re-open →
        </button>
        <button
          type="button"
          onClick={() => endGeneration(g.id)}
          title="Dismiss — the build keeps running on the server"
          className="px-3 py-1 font-mono text-[9px] tracking-[0.16em] uppercase rounded-sm text-ink-dim hover:text-terracotta transition-colors whitespace-nowrap"
        >
          ✕ Dismiss
        </button>
      </div>
      <style>{`
        @keyframes cookHalo {
          0%, 100% { box-shadow: 0 16px 36px -10px rgba(45,28,20,0.42), 0 0 0 5px rgba(194,85,56,0.10); }
          50%      { box-shadow: 0 18px 42px -10px rgba(45,28,20,0.46), 0 0 0 9px rgba(194,85,56,0.16); }
        }
        @keyframes cookDot {
          0%, 100% { opacity: 0.55; transform: scale(0.92); }
          50%      { opacity: 1;    transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
}
